import {
  Column,
  InputWithAddon,
  Row,
  TabPane,
  TabPanel,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { paths } from '@/routes/paths'
import { useLocationTypes } from '@/services/locationTypes'
import { theme } from '@/theme'
import { Insurance } from '@/types/insurance'
import { useGoToPath } from '@/utils'
import { Button, Skeleton } from '@surein/ui'
import { ChevronRight, FileMinus, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  CardWrapper,
  CoverageGrid,
  CoverageTitleSection,
  EmptyStateContainer,
  SectionWrapper,
} from '../styles'
import { CoverageCard } from './CoverageCard'
import { useInsuranceSearch } from './useInsuranceSearch'

const SkeletonCoverageCard = () => {
  return (
    <CardWrapper>
      <Column gap="64px">
        <Skeleton count={3} withParagraphEnding />
        <Column gap="16px">
          <Row gap="16px">
            <Skeleton withParagraphEnding />
            <Skeleton withParagraphEnding />
          </Row>
          <Row gap="16px">
            <Skeleton />
            <Skeleton />
          </Row>
        </Column>
      </Column>
    </CardWrapper>
  )
}

const SectionSkeleton = () => {
  return (
    <>
      <SkeletonCoverageCard />
      <SkeletonCoverageCard />
    </>
  )
}

const AddContractButtonIcon = () => (
  <ChevronRight size="16" color={theme.color.info1} />
)

const SectionEmptyState = ({ archived }: { archived: boolean }) => {
  const { t } = useTranslation()
  const { locationId } = useParams<{ locationId: string }>()
  const goToFindInsurances = useGoToPath(
    paths.findInsurancesWithKey(locationId || ''),
  )

  return (
    <EmptyStateContainer>
      <FileMinus size={32} color={theme.color.b50} />
      <Typography bold variant="p1Body">
        {t(
          `locationPage.${
            archived
              ? 'inactiveCoverageEmptyStateTitle'
              : 'activeCoverageEmptyStateTitle'
          }`,
        )}
      </Typography>
      <Typography variant="p2Body" color={ThemeColor.b50}>
        {t(
          `locationPage.${
            archived
              ? 'inactiveCoverageEmptyStateDescription'
              : 'activeCoverageEmptyStateDescription'
          }`,
        )}
      </Typography>
      {!archived && (
        <Button
          variant="link"
          size="xs"
          onClick={() => goToFindInsurances()}
          suffixIcon={AddContractButtonIcon}
        >
          <Typography bold variant="p2Body" color={ThemeColor.info1}>
            {t('locationPage.addContract')}
          </Typography>
        </Button>
      )}
    </EmptyStateContainer>
  )
}

interface TabContentProps {
  insurances: Insurance[]
  isLoading: boolean
  locationId: string
  getLocationLabel: (risksInsured: string) => string
  archived: boolean
}

const TabContent = ({
  insurances,
  isLoading,
  locationId,
  getLocationLabel,
  archived,
}: TabContentProps) => {
  return (
    <CoverageGrid>
      {isLoading ? (
        <SectionSkeleton />
      ) : insurances.length === 0 ? (
        <SectionEmptyState archived={archived} />
      ) : (
        insurances.map((insurance) => (
          <CoverageCard
            key={insurance.insuranceId}
            carrierName={insurance.carrierName}
            categoryId={insurance.categoryId}
            logoUrl={insurance.logoUrl}
            status={insurance.contractStatus}
            grossPrice={insurance.grossPrice}
            risksInsured={insurance.risksInsured}
            paymentPeriod={insurance.paymentPeriod}
            insuranceId={insurance.insuranceId}
            locationId={locationId}
            getLocationLabel={getLocationLabel}
          />
        ))
      )}
    </CoverageGrid>
  )
}

interface CoverageSectionProps {
  insurances: Insurance[]
  isLoading: boolean
  locationId: string
  archived: boolean
}

export const CoverageSection = ({
  insurances,
  isLoading,
  locationId,
  archived,
}: CoverageSectionProps) => {
  const { t } = useTranslation()
  const { debouncedSearch, filteredInsurances } = useInsuranceSearch(insurances)
  const {
    data: locationTypesData,
    isLoading: locationTypesLoading,
  } = useLocationTypes()

  const loading = isLoading || locationTypesLoading

  const getLocationLabel = locationTypesData?.getLocationLabel || (() => '')

  return (
    <SectionWrapper>
      <Column gap="16px">
        <CoverageTitleSection
          gap="16px"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography bold variant="h4">
            {t('locationPage.coverage')}
          </Typography>
          <InputWithAddon
            addonBefore={() => <SearchIcon color={theme.color.b40} size={20} />}
            onChange={(e) => debouncedSearch(e.target.value)}
            placeholder={t('locationPage.searchByCoverage')}
          />
        </CoverageTitleSection>
        <TabPanel color={ThemeColor.info1} queryParamKey="archived">
          <TabPane key="false" text={t('locationPage.active')}>
            <TabContent
              insurances={filteredInsurances}
              isLoading={loading}
              locationId={locationId}
              getLocationLabel={getLocationLabel}
              archived={archived}
            />
          </TabPane>
          <TabPane key="true" text={t('locationPage.inactive')}>
            <TabContent
              insurances={filteredInsurances}
              isLoading={loading}
              locationId={locationId}
              getLocationLabel={getLocationLabel}
              archived={archived}
            />
          </TabPane>
        </TabPanel>
      </Column>
    </SectionWrapper>
  )
}
